import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Auth } from 'aws-amplify';
import getEnvVar from '@/utils/getEnvVar';

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function isRetryableRequest(config: unknown): config is RetryableRequest {
  return config != null && typeof config === 'object';
}

const userProfileHttpClientUrl = getEnvVar('VITE_DATA_API_URL');

export const userProfileHttpClient = axios.create({
  baseURL: userProfileHttpClientUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

userProfileHttpClient.interceptors.request.use(
  async function (config) {
    const authUser = await Auth.currentAuthenticatedUser();
    config.headers.Authorization = authUser.getSignInUserSession().getIdToken().getJwtToken();

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function drainQueue(token: string | null, error: unknown) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
}

userProfileHttpClient.interceptors.response.use(
  response => response,
  async function (error: AxiosError) {
    if (!isRetryableRequest(error.config)) return Promise.reject(error);
    const originalRequest: RetryableRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = token;
          return userProfileHttpClient(originalRequest);
        });
      }

      isRefreshing = true;
      try {
        const authUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
        const newToken = authUser.getSignInUserSession().getIdToken().getJwtToken();
        drainQueue(newToken, null);
        originalRequest.headers.Authorization = newToken;
        return await userProfileHttpClient(originalRequest);
      } catch (refreshError) {
        drainQueue(null, refreshError);
        Auth.signOut();
        return await Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
